import { useState, useRef } from "react";

const T = {
  bg:"#0B0F1A", surface:"#111827", card:"#1A2235", border:"#1E2D45",
  accent:"#38BDF8", green:"#34D399", yellow:"#FCD34D", red:"#F87171",
  purple:"#A78BFA", orange:"#FB923C", textPri:"#F0F6FF", textSec:"#94A3B8",
  textMute:"#4B5563", codeBg:"#060D18", codeStr:"#86EFAC", codeKw:"#7DD3FC",
  codeFn:"#FDE68A", codeCmt:"#475569", codeNum:"#F9A8D4",
  ora:"#FB923C", pg:"#38BDF8",  // Oracle = orange, Postgres = blue
};

const ORA_KWS = ["sqlplus","sysdba","SPOOL","DESC","ROWNUM","SYSDATE","SYSTIMESTAMP",
  "NVL","DECODE","dual","NUMBER","VARCHAR2","CLOB","BLOB","NEXTVAL","CURRVAL",
  "FETCH","expdp","impdp","v$session","v$version","v$pdbs","dba_users","all_tables",
  "user_tables","user_indexes","user_segments","DBMS_STATS","DBMS_XPLAN","spfile",
  "GATHER_TABLE_STATS","IDENTIFIED","sysdba","CONTAINER","COMMIT","ROLLBACK","SAVEPOINT"];
const PG_KWS  = ["psql","pg_dump","pg_terminate_backend","pg_stat_activity",
  "pg_size_pretty","pg_total_relation_size","pg_database","pg_tables","pg_user",
  "ILIKE","SERIAL","BYTEA","JSONB","TEXT","BOOLEAN","ANALYZE","INTERVAL",
  "current_user","current_database","current_schema","datname","schemaname",
  "tablename","usename","\\l","\\c","\\dt","\\d","\\du","\\di","\\q","\\i","\\o"];
const SQL_KWS = ["SELECT","FROM","WHERE","INSERT","UPDATE","DELETE","CREATE","DROP",
  "ALTER","TABLE","INDEX","TRUNCATE","DATABASE","USER","SCHEMA","GRANT","REVOKE",
  "INTO","VALUES","SET","JOIN","ON","AS","AND","OR","NOT","IN","IS","NULL","WITH",
  "ORDER","BY","GROUP","HAVING","LIMIT","OFFSET","BEGIN","EXPLAIN","SEQUENCE",
  "PRIMARY","KEY","REFERENCES","FOREIGN","UNIQUE","IF","EXISTS","CASCADE",
  "PASSWORD","GENERATED","ALWAYS","IDENTITY","ADD","COLUMN","TYPE","MODIFY",
  "RENAME","NEXT","CURRENT","PROCEDURE","FUNCTION","LANGUAGE","RETURNS","plpgsql"];

function highlight(code) {
  return code.split("\n").map((line, li) => {
    if (line.trimStart().startsWith("--")) {
      return <div key={li} style={{minHeight:"1.4em",color:T.codeCmt}}>{line}</div>;
    }
    const tokens = [];
    let i = 0;
    while (i < line.length) {
      if (line[i] === "#" && (i===0||line[i-1]===" "||line[i-1]==="\t")) {
        tokens.push(<span key={i} style={{color:T.codeCmt}}>{line.slice(i)}</span>);
        i = line.length; continue;
      }
      if (line[i] === "'" || line[i] === '"') {
        const q = line[i]; let j = i+1;
        while (j<line.length && line[j]!==q) j++;
        tokens.push(<span key={i} style={{color:T.codeStr}}>{line.slice(i,j+1)}</span>);
        i = j+1; continue;
      }
      if (/\d/.test(line[i]) && (i===0||!/\w/.test(line[i-1]))) {
        let j = i; while(j<line.length && /[\d._]/.test(line[j])) j++;
        tokens.push(<span key={i} style={{color:T.codeNum}}>{line.slice(i,j)}</span>);
        i = j; continue;
      }
      if (/[a-zA-Z_\\$]/.test(line[i])) {
        let j = i; while(j<line.length && /[\w$\\*.]/.test(line[j])) j++;
        const word = line.slice(i,j);
        const up = word.toUpperCase();
        let color = T.textPri;
        if (SQL_KWS.includes(up)) color = T.codeKw;
        else if (ORA_KWS.some(k=>up===k.toUpperCase())) color = T.ora;
        else if (PG_KWS.some(k=>up===k.toUpperCase()||word===k)) color = T.pg;
        tokens.push(<span key={i} style={{color}}>{word}</span>);
        i = j; continue;
      }
      tokens.push(<span key={i} style={{color:T.textSec}}>{line[i]}</span>);
      i++;
    }
    return <div key={li} style={{minHeight:"1.4em"}}>{tokens}</div>;
  });
}

function CodeBlock({ code, label="sql" }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{marginBottom:16,borderRadius:10,overflow:"hidden",border:`1px solid ${T.border}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
        background:"#0D1929",padding:"6px 14px",borderBottom:`1px solid ${T.border}`}}>
        <span style={{fontSize:11,color:T.accent,fontFamily:"monospace",letterSpacing:.5}}>{label}</span>
        <button onClick={()=>{navigator.clipboard.writeText(code);setCopied(true);setTimeout(()=>setCopied(false),1500);}}
          style={{fontSize:11,color:copied?T.green:T.textSec,background:"none",border:"none",cursor:"pointer",padding:"2px 8px",borderRadius:4}}>
          {copied?"✓ copied":"copy"}
        </button>
      </div>
      <div style={{background:T.codeBg,padding:"14px 16px",overflowX:"auto",
        fontFamily:"'JetBrains Mono','Fira Mono',monospace",fontSize:12.5,lineHeight:1.65}}>
        {highlight(code)}
      </div>
    </div>
  );
}

function Concept({ children }) {
  return (
    <div style={{background:T.card,borderLeft:`3px solid ${T.accent}`,borderRadius:"0 8px 8px 0",
      padding:"12px 16px",marginBottom:14,fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
      {children}
    </div>
  );
}
function WarnBox({ items }) {
  return (
    <div style={{background:"#1C1506",border:"1px solid #78350F",borderRadius:8,padding:"12px 16px",marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:600,color:T.yellow,letterSpacing:1,marginBottom:8,textTransform:"uppercase"}}>⚠ Gotcha</div>
      {items.map((it,i)=><div key={i} style={{fontSize:13,color:"#D4B483",marginBottom:5,lineHeight:1.5}}>{it}</div>)}
    </div>
  );
}
function InfoBox({ title, color=T.accent, bgColor="#071929", borderColor="#1E3A52", children }) {
  return (
    <div style={{background:bgColor,border:`1px solid ${borderColor}`,borderRadius:8,padding:"12px 16px",marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:600,color,letterSpacing:1,marginBottom:8,textTransform:"uppercase"}}>{title}</div>
      <div style={{fontSize:13,color:T.textSec,lineHeight:1.7}}>{children}</div>
    </div>
  );
}

// Two-tone badge for Oracle / PG labels in table cells
function O({ children }) {
  return <code style={{background:"#2D1A06",color:T.ora,padding:"1px 5px",borderRadius:4,fontSize:11.5,fontFamily:"'JetBrains Mono',monospace"}}>{children}</code>;
}
function P({ children }) {
  return <code style={{background:"#071929",color:T.pg,padding:"1px 5px",borderRadius:4,fontSize:11.5,fontFamily:"'JetBrains Mono',monospace"}}>{children}</code>;
}

function TTable({ headers, rows }) {
  return (
    <div style={{overflowX:"auto",marginBottom:16}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12.5}}>
        <thead>
          <tr>{headers.map((h,i)=>(
            <th key={i} style={{background:"#0D1929",color:T.accent,padding:"8px 12px",
              borderBottom:`1px solid ${T.border}`,textAlign:"left",fontWeight:600,whiteSpace:"nowrap"}}>
              {h}
            </th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.map((row,ri)=>(
            <tr key={ri} style={{background:ri%2===0?T.card:T.surface}}>
              {row.map((cell,ci)=>(
                <td key={ci} style={{padding:"7px 12px",color:T.textSec,
                  borderBottom:`1px solid ${T.border}33`,lineHeight:1.6,verticalAlign:"top"}}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── SECTION COMPONENTS ────────────────────────────────────────────────────────

function S1() { return (
  <div>
    <Concept>
      Two different CLI clients, two different default ports, two very different "admin" connection styles.
      <br/>Rule: <strong style={{color:T.ora}}>Oracle = SQL*Plus</strong> — <strong style={{color:T.pg}}>PostgreSQL = psql</strong>
    </Concept>
    <TTable
      headers={["Task", <span style={{color:T.ora}}>Oracle (SQL*Plus)</span>, <span style={{color:T.pg}}>PostgreSQL (psql)</span>]}
      rows={[
        ["Connect", <O>sqlplus user/pass@//host:1521/service</O>, <P>psql -h host -p 5432 -U user -d dbname</P>],
        ["Connect as admin", <O>sqlplus / as sysdba</O>, <P>psql -U postgres</P>],
        ["Default port", <O>1521</O>, <P>5432</P>],
        ["Exit client", <O>EXIT;</O>, <P>\q</P>],
        ["Run a script file", <O>@script.sql</O>, <P>\i script.sql</P>],
        ["Spool output to file", <O>SPOOL out.txt … SPOOL OFF</O>, <P>\o out.txt … \o</P>],
      ]}
    />
    <CodeBlock code={`-- Oracle
sqlplus user/pass@//host:1521/ORCLPDB1
sqlplus / as sysdba          -- local OS-authenticated admin

-- PostgreSQL
psql -h localhost -p 5432 -U myuser -d mydb
psql -U postgres             -- superuser shorthand`} label="shell — connect" />
    <WarnBox items={[
      "Oracle's admin login is OS-level: 'sqlplus / as sysdba' works only from the DB server itself (or with OS auth). PostgreSQL -U postgres requires the postgres OS user or pg_hba.conf to allow it.",
      "PostgreSQL's \\-commands (\\q, \\dt, \\d) are psql meta-commands — they are NOT SQL and won't work if you send them through a JDBC/ODBC connection.",
    ]} />
  </div>
);}

function S2() { return (
  <div>
    <Concept>
      Oracle has a 3-tier data dictionary: <strong style={{color:T.ora}}>USER_*</strong> (my objects) →
      <strong style={{color:T.ora}}> ALL_*</strong> (objects I can see) →
      <strong style={{color:T.ora}}> DBA_*</strong> (everything).
      PostgreSQL uses <strong style={{color:T.pg}}>pg_catalog</strong> system tables and backslash meta-commands.
    </Concept>
    <TTable
      headers={["Task", <span style={{color:T.ora}}>Oracle</span>, <span style={{color:T.pg}}>PostgreSQL</span>]}
      rows={[
        ["List databases", <O>SELECT name FROM v$pdbs;</O>, <><P>\l</P>{" or "}<P>SELECT datname FROM pg_database;</P></>],
        ["Switch database", <O>ALTER SESSION SET CONTAINER = pdb1;</O>, <P>\c dbname</P>],
        ["List my tables", <O>SELECT table_name FROM user_tables;</O>, <><P>\dt</P>{" or "}<P>SELECT tablename FROM pg_tables WHERE schemaname='public';</P></>],
        ["List all visible tables", <O>SELECT table_name FROM all_tables;</O>, <P>\dt *.*</P>],
        ["Describe a table", <O>DESC employees;</O>, <P>\d employees</P>],
        ["List users", <O>SELECT username FROM dba_users;</O>, <><P>\du</P>{" or "}<P>SELECT usename FROM pg_user;</P></>],
        ["Current user", <O>SELECT user FROM dual;</O>, <P>SELECT current_user;</P>],
        ["Current schema/db", <O>SELECT sys_context('USERENV','CURRENT_SCHEMA') FROM dual;</O>, <P>SELECT current_database(), current_schema();</P>],
        ["DB version", <O>SELECT * FROM v$version;</O>, <P>SELECT version();</P>],
        ["List indexes on table", <O>SELECT index_name FROM user_indexes WHERE table_name='EMPLOYEES';</O>, <><P>\di</P>{" or "}<P>\d employees</P></>],
      ]}
    />
    <InfoBox title="Key Concept — Data Dictionary Tiers (Oracle)">
      <TTable headers={["Prefix","Scope"]} rows={[
        [<O>USER_*</O>, "Objects owned by me (user_tables, user_indexes, user_sequences)"],
        [<O>ALL_*</O>,  "Objects I have been granted access to (all_tables, all_objects)"],
        [<O>DBA_*</O>,  "Everything in the database — requires DBA privilege (dba_users, dba_segments)"],
      ]} />
      PostgreSQL equivalent: <P>pg_catalog</P> system tables + <P>information_schema</P> views
      (Oracle 23c+ partially supports information_schema too).
    </InfoBox>
  </div>
);}

function S3() { return (
  <div>
    <Concept>
      The biggest conceptual gap between Oracle and PostgreSQL:
      in Oracle a <strong style={{color:T.ora}}>user IS the schema</strong>.
      In PostgreSQL they are <strong style={{color:T.pg}}>completely separate</strong> — a user can own objects in any schema.
    </Concept>
    <TTable
      headers={["Task", <span style={{color:T.ora}}>Oracle</span>, <span style={{color:T.pg}}>PostgreSQL</span>]}
      rows={[
        ["Create database", "Usually via DBCA/installer (heavyweight)", <P>CREATE DATABASE shopdb;</P>],
        ["Create user", <O>CREATE USER app IDENTIFIED BY secret;</O>, <P>CREATE USER app WITH PASSWORD 'secret';</P>],
        ["User vs schema", <span>User <strong>is</strong> the schema — creating a user creates a schema</span>, <span>Separate: <P>CREATE SCHEMA sales;</P></span>],
        ["Grant login/connect", <O>GRANT CREATE SESSION TO app;</O>, "Granted by default (LOGIN attribute)"],
        ["Grant table access", <O>GRANT SELECT, INSERT ON employees TO app;</O>, "Same SQL syntax"],
        ["Grant all on schema", "Grant object-by-object (or roles)", <P>GRANT ALL ON ALL TABLES IN SCHEMA public TO app;</P>],
        ["Change password", <O>ALTER USER app IDENTIFIED BY newpass;</O>, <P>ALTER USER app WITH PASSWORD 'newpass';</P>],
        ["Drop user", <O>DROP USER app CASCADE;</O>, <P>DROP USER app;</P>],
      ]}
    />
    <CodeBlock code={`-- Oracle: user creation = schema creation
CREATE USER hr IDENTIFIED BY hr123;
GRANT CREATE SESSION, CREATE TABLE, CREATE SEQUENCE TO hr;
-- hr.employees, hr.departments — "hr" is both user AND schema name

-- PostgreSQL: users and schemas are independent
CREATE USER hr WITH PASSWORD 'hr123';
CREATE SCHEMA hr AUTHORIZATION hr;   -- optional: a schema named hr
GRANT USAGE ON SCHEMA hr TO hr;
GRANT ALL ON ALL TABLES IN SCHEMA hr TO hr;`} label="sql — users and schemas" />
    <WarnBox items={[
      "Oracle: DROP USER app; fails if the user owns objects. Use DROP USER app CASCADE; to drop user + all their objects.",
      "PostgreSQL: DROP USER fails if user owns any objects or has privileges. You must DROP SCHEMA ... CASCADE or reassign ownership first.",
    ]} />
  </div>
);}

function S4() { return (
  <div>
    <Concept>
      Core DDL (CREATE TABLE, ALTER TABLE, DROP TABLE, indexes) is nearly identical.
      The differences are in column type modification syntax and a few edge cases.
    </Concept>
    <InfoBox title="Same in Both Databases">
      <CodeBlock code={`CREATE TABLE employees (
  emp_id    INT PRIMARY KEY,
  name      VARCHAR(100) NOT NULL,
  salary    NUMERIC(10,2),
  hire_date DATE
);

ALTER TABLE employees ADD department VARCHAR(50);
ALTER TABLE employees DROP COLUMN department;
DROP TABLE employees;
TRUNCATE TABLE employees;
CREATE INDEX idx_emp_name ON employees(name);`} label="sql — common DDL" />
    </InfoBox>
    <TTable
      headers={["Task", <span style={{color:T.ora}}>Oracle</span>, <span style={{color:T.pg}}>PostgreSQL</span>]}
      rows={[
        ["Rename column",
          <O>ALTER TABLE t RENAME COLUMN old TO new;</O>,
          "Same syntax"],
        ["Modify column type",
          <O>ALTER TABLE t MODIFY (col VARCHAR2(200));</O>,
          <P>ALTER TABLE t ALTER COLUMN col TYPE VARCHAR(200);</P>],
        ["Drop table if exists",
          <><O>DROP TABLE t;</O>{" (23c+: "}<O>DROP TABLE IF EXISTS t;</O>{")"}</>,
          <P>DROP TABLE IF EXISTS t;</P>],
        ["Copy table + data (CTAS)",
          <O>CREATE TABLE t2 AS SELECT * FROM t;</O>,
          "Same syntax"],
      ]}
    />
    <WarnBox items={[
      "Oracle: DDL statements (CREATE, DROP, ALTER) auto-commit — you cannot roll them back. PostgreSQL DDL is transactional — you can wrap CREATE TABLE in BEGIN/ROLLBACK.",
      "Oracle uses MODIFY to change column type, PostgreSQL uses ALTER COLUMN … TYPE. Both will fail if existing data cannot be cast.",
    ]} />
  </div>
);}

function S5() { return (
  <div>
    <Concept>
      Type mapping is the most common porting headache. The biggest trap: Oracle's <O>DATE</O> stores
      date <em>and</em> time — PostgreSQL's <P>DATE</P> is date only.
    </Concept>
    <TTable
      headers={["Concept", <span style={{color:T.ora}}>Oracle</span>, <span style={{color:T.pg}}>PostgreSQL</span>]}
      rows={[
        ["Variable text", <O>VARCHAR2(100)</O>, <><P>VARCHAR(100)</P>{" or "}<P>TEXT</P>{" (unlimited)"}</>],
        ["Whole number", <O>NUMBER(10)</O>, <><P>INT</P>{" / "}<P>BIGINT</P></>],
        ["Decimal", <O>NUMBER(10,2)</O>, <P>NUMERIC(10,2)</P>],
        ["Date + time",
          <><O>DATE</O>{" — "}<strong style={{color:T.orange}}>includes time!</strong></>,
          <><P>TIMESTAMP</P>{" (DATE = date only)"}</>],
        ["Auto-increment",
          <><O>GENERATED AS IDENTITY</O>{" (12c+) or SEQUENCE + trigger"}</>,
          <><P>GENERATED AS IDENTITY</P>{" or "}<P>SERIAL</P></>],
        ["Boolean",
          "None before 23c — use NUMBER(1) or CHAR(1)",
          <><P>BOOLEAN</P>{" (true / false)"}</>],
        ["Large text", <O>CLOB</O>, <P>TEXT</P>],
        ["Binary", <O>BLOB</O>, <P>BYTEA</P>],
        ["JSON",
          <><O>JSON</O>{" (21c+) / VARCHAR2 + check constraints"}</>,
          <><P>JSON</P>{" / "}<P>JSONB</P>{" (jsonb preferred — indexed)"}</>],
        ["Cast syntax", <O>TO_NUMBER(col)</O>, <><P>col::numeric</P>{" or "}<P>CAST(col AS numeric)</P></>],
      ]}
    />
    <WarnBox items={[
      "Oracle DATE = date + time. If your Oracle query uses TRUNC(hire_date) to strip the time, in PostgreSQL use hire_date::date or DATE_TRUNC('day', hire_date).",
      "Oracle VARCHAR2 treats empty string '' as NULL. PostgreSQL treats them as different. A WHERE col IS NULL query may behave differently after migration.",
      "Prefer JSONB over JSON in PostgreSQL — it's stored in binary, indexed with GIN indexes, and operators like @> (contains) work on it.",
    ]} />
  </div>
);}

function S6() { return (
  <div>
    <Concept>
      Modern syntax (<O>GENERATED AS IDENTITY</O>) works in both Oracle 12c+ and PostgreSQL 10+
      and is the recommended approach. Classic patterns (<O>SEQUENCE</O> in Oracle, <P>SERIAL</P> in
      PostgreSQL) still appear everywhere in legacy code.
    </Concept>
    <InfoBox title="Modern — Preferred (both databases)">
      <CodeBlock code={`CREATE TABLE orders (
  order_id INT GENERATED ALWAYS AS IDENTITY PRIMARY KEY,
  item     VARCHAR(50)
);
-- Works in Oracle 12c+ AND PostgreSQL 10+`} label="sql" />
    </InfoBox>
    <InfoBox title="Classic Oracle — SEQUENCE" color={T.ora} bgColor="#1a0f06" borderColor="#78350F">
      <CodeBlock code={`-- Create a sequence
CREATE SEQUENCE order_seq START WITH 1 INCREMENT BY 1;

-- Use it on INSERT
INSERT INTO orders (order_id, item)
VALUES (order_seq.NEXTVAL, 'Pen');

-- Peek at current value (only valid after at least one NEXTVAL in the session)
SELECT order_seq.CURRVAL FROM dual;`} label="sql — Oracle sequence" />
    </InfoBox>
    <InfoBox title="Classic PostgreSQL — SERIAL" color={T.pg} bgColor="#071929" borderColor="#1E3A52">
      <CodeBlock code={`-- SERIAL is shorthand: creates a sequence + sets column default
CREATE TABLE orders (
  order_id SERIAL PRIMARY KEY,   -- expands to INT + sequence + DEFAULT nextval(...)
  item     VARCHAR(50)
);

-- Sequence functions
SELECT nextval('orders_order_id_seq');
SELECT currval('orders_order_id_seq');
SELECT lastval();   -- last value returned by nextval in this session`} label="sql — PostgreSQL serial/sequence" />
    </InfoBox>
    <TTable
      headers={["Sequence task", <span style={{color:T.ora}}>Oracle</span>, <span style={{color:T.pg}}>PostgreSQL</span>]}
      rows={[
        ["Next value", <O>seq.NEXTVAL</O>, <P>nextval('seq')</P>],
        ["Current value", <O>seq.CURRVAL</O>, <P>currval('seq')</P>],
        ["Last used in session", "(not available)", <P>lastval()</P>],
        ["Reset sequence", <O>ALTER SEQUENCE seq RESTART START WITH 1;</O>, <P>ALTER SEQUENCE seq RESTART WITH 1;</P>],
      ]}
    />
  </div>
);}

function S7() { return (
  <div>
    <Concept>
      Standard SQL (SELECT / JOINs / GROUP BY / aggregates) is identical.
      These are the <strong style={{color:T.yellow}}>dialect traps</strong> that silently break when you port a query.
    </Concept>
    <TTable
      headers={["Task", <span style={{color:T.ora}}>Oracle</span>, <span style={{color:T.pg}}>PostgreSQL</span>]}
      rows={[
        ["Select without a table",
          <O>SELECT 1+1 FROM dual;</O>,
          <P>SELECT 1+1;{" "}</P>],
        ["First 10 rows",
          <><O>FETCH FIRST 10 ROWS ONLY</O>{" (12c+) or "}<O>WHERE ROWNUM &lt;= 10</O></>,
          <P>SELECT * FROM t LIMIT 10;</P>],
        ["Rows 11–20 (paging)",
          <O>OFFSET 10 ROWS FETCH NEXT 10 ROWS ONLY</O>,
          <P>LIMIT 10 OFFSET 10</P>],
        ["Current date/time",
          <><O>SYSDATE</O>{", "}<O>SYSTIMESTAMP</O></>,
          <><P>CURRENT_DATE</P>{", "}<P>NOW()</P></>],
        ["Null replacement",
          <O>NVL(col, 0)</O>,
          <><P>COALESCE(col, 0)</P>{" (works in both — prefer it)"}</>],
        ["If/else in query",
          <><O>DECODE(...)</O>{" or CASE WHEN"}</>,
          "CASE WHEN (standard, works in both)"],
        ["String concat",
          <><O>'a' || 'b'</O>{" or "}<O>CONCAT</O>{" (2 args only)"}</>,
          <><P>'a' || 'b'</P>{" or "}<P>CONCAT</P>{" (multi-arg)"}</>],
        ["Substring",
          <O>SUBSTR(s, 1, 3)</O>,
          <><P>SUBSTR(s, 1, 3)</P>{" or "}<P>SUBSTRING(s FROM 1 FOR 3)</P></>],
        ["Date to string",
          <O>TO_CHAR(SYSDATE, 'YYYY-MM-DD')</O>,
          <P>TO_CHAR(NOW(), 'YYYY-MM-DD')</P>],
        ["String to date",
          <O>TO_DATE('2026-07-18', 'YYYY-MM-DD')</O>,
          <><P>TO_DATE(...)</P>{" or "}<P>'2026-07-18'::date</P></>],
        ["Add 7 days",
          <O>SYSDATE + 7</O>,
          <P>NOW() + INTERVAL '7 days'</P>],
        ["Case-insensitive search",
          <O>UPPER(col) LIKE 'A%'</O>,
          <P>col ILIKE 'a%'</P>],
        ["Empty string vs NULL",
          <><O>'' IS NULL</O>{" → "}<strong style={{color:T.orange}}>TRUE</strong>{" (!)"}</>,
          <span><P>''</P>{" and "}<P>NULL</P>{" are different"}</span>],
      ]}
    />
    <InfoBox title="Write Portable SQL — prefer these forms in both databases" color={T.green} bgColor="#0a1a10" borderColor="#1E3A52">
      <TTable headers={["❌ Oracle-only","❌ PostgreSQL-only","✅ Portable"]} rows={[
        [<O>NVL(x, 0)</O>,          "(not needed)", <P>COALESCE(x, 0)</P>],
        [<O>DECODE(col,...)</O>,     "(not supported)", "CASE WHEN col = ... THEN ... END"],
        [<O>WHERE ROWNUM &lt;= 10</O>, <P>LIMIT 10</P>, "FETCH FIRST 10 ROWS ONLY"],
        [<O>SYSDATE + 7</O>,        <P>NOW() + INTERVAL '7 days'</P>, "CURRENT_TIMESTAMP + INTERVAL '7' DAY (ANSI)"],
        [<O>FROM dual</O>,           "(not needed)", "FROM (VALUES (1)) t(x) or just omit"],
      ]} />
    </InfoBox>
    <WarnBox items={[
      "Oracle: '' (empty string) IS NULL → true. Storing '' in Oracle saves NULL. This can break WHERE col = '' queries when ported to PostgreSQL.",
      "Oracle ROWNUM is assigned before ORDER BY. Wrapping SELECT * FROM (SELECT ... ORDER BY sal) WHERE ROWNUM <= 10 is the old safe pattern. Just use FETCH FIRST in Oracle 12c+.",
      "PostgreSQL ILIKE is case-insensitive LIKE. Oracle has no direct equivalent — use UPPER/LOWER or a function-based index.",
    ]} />
  </div>
);}

function S8() { return (
  <div>
    <Concept>
      The transaction model is completely opposite by default:
      Oracle is <strong style={{color:T.ora}}>commit-on-demand</strong>,
      PostgreSQL psql is <strong style={{color:T.pg}}>autocommit-on</strong>.
      The most important difference: Oracle DDL auto-commits, PostgreSQL DDL is transactional.
    </Concept>
    <TTable
      headers={["Behavior", <span style={{color:T.ora}}>Oracle</span>, <span style={{color:T.pg}}>PostgreSQL</span>]}
      rows={[
        ["Autocommit (CLI)", <span style={{color:T.orange}}>OFF — must COMMIT manually</span>, <span style={{color:T.pg}}>ON — each statement auto-commits in psql</span>],
        ["Start transaction", "Implicit — first DML statement starts one", <P>BEGIN;</P>],
        ["Commit / Rollback", <O>COMMIT;</O>, <P>COMMIT;</P>],
        ["Savepoints", <O>SAVEPOINT sp1; ROLLBACK TO sp1;</O>, "Same syntax"],
        ["DDL in transaction",
          <span><strong style={{color:T.orange}}>DDL auto-commits</strong> — cannot roll back CREATE TABLE</span>,
          <span><strong style={{color:T.pg}}>DDL is transactional</strong> — can roll back!</span>],
      ]}
    />
    <CodeBlock code={`-- Oracle: must COMMIT or changes are lost on disconnect
INSERT INTO orders (item) VALUES ('Pen');
INSERT INTO orders (item) VALUES ('Book');
COMMIT;   -- ← without this, disconnect = rows gone

SAVEPOINT before_delete;
DELETE FROM orders WHERE item = 'Pen';
ROLLBACK TO before_delete;  -- undo just the delete
COMMIT;                     -- commit the two inserts`} label="sql — Oracle transactions" />
    <CodeBlock code={`-- PostgreSQL: autocommit by default in psql
INSERT INTO orders (item) VALUES ('Pen');   -- auto-committed!

-- To group multiple statements:
BEGIN;
  INSERT INTO orders (item) VALUES ('Pen');
  INSERT INTO orders (item) VALUES ('Book');
  SAVEPOINT before_delete;
  DELETE FROM orders WHERE item = 'Pen';
  ROLLBACK TO before_delete;
COMMIT;

-- PostgreSQL: DDL is transactional!
BEGIN;
  CREATE TABLE temp_test (id INT);
  -- changed my mind:
ROLLBACK;   -- table was never created ✅`} label="sql — PostgreSQL transactions" />
    <WarnBox items={[
      "Classic Oracle trap: you INSERT rows, everything looks fine in your session, you disconnect — rows are gone because you never ran COMMIT. Oracle does NOT auto-commit DML.",
      "PostgreSQL in psql auto-commits each statement. In application code (JDBC, psycopg2, etc.) autocommit depends on the driver setting — check it explicitly.",
      "Never put a COMMIT inside a stored procedure/function if you want the caller to be able to roll it back. This applies to both databases.",
    ]} />
  </div>
);}

function S9() { return (
  <div>
    <Concept>
      DBA / admin tasks have very different commands but the same intent: backup, kill sessions, inspect activity, gather stats.
    </Concept>
    <TTable
      headers={["Task", <span style={{color:T.ora}}>Oracle</span>, <span style={{color:T.pg}}>PostgreSQL</span>]}
      rows={[
        ["Logical backup",
          <O>expdp / impdp (Data Pump)</O>,
          <><P>pg_dump dbname {'>'} file.sql</P>{" / "}<P>psql {'<'} file.sql</P></>],
        ["Kill a session",
          <O>ALTER SYSTEM KILL SESSION 'sid,serial#';</O>,
          <P>SELECT pg_terminate_backend(pid);</P>],
        ["Active sessions",
          <O>SELECT * FROM v$session;</O>,
          <P>SELECT * FROM pg_stat_activity;</P>],
        ["Table size",
          <O>SELECT bytes FROM user_segments WHERE segment_name='T';</O>,
          <P>SELECT pg_size_pretty(pg_total_relation_size('t'));</P>],
        ["Explain a query",
          <><O>EXPLAIN PLAN FOR SELECT ...;</O><br/><O>SELECT * FROM TABLE(DBMS_XPLAN.DISPLAY);</O></>,
          <P>EXPLAIN ANALYZE SELECT ...;</P>],
        ["Update optimizer stats",
          <O>EXEC DBMS_STATS.GATHER_TABLE_STATS(user,'T');</O>,
          <P>ANALYZE t;</P>],
        ["Config file",
          <O>spfile / init.ora</O>,
          <><P>postgresql.conf</P>{" (settings), "}<P>pg_hba.conf</P>{" (auth)"}</>],
        ["Procedural language",
          <O>PL/SQL</O>,
          <P>PL/pgSQL</P>],
      ]}
    />
    <CodeBlock code={`-- Oracle: find and kill a blocking session
SELECT sid, serial#, username, status, sql_id
FROM   v$session
WHERE  status = 'ACTIVE' AND username IS NOT NULL;

ALTER SYSTEM KILL SESSION '142,3891' IMMEDIATE;

-- Oracle: explain
EXPLAIN PLAN FOR
  SELECT * FROM employees WHERE department_id = 10;
SELECT * FROM TABLE(DBMS_XPLAN.DISPLAY);

-- Oracle: gather stats
EXEC DBMS_STATS.GATHER_TABLE_STATS(ownname=>USER, tabname=>'EMPLOYEES', cascade=>TRUE);`} label="sql — Oracle admin" />
    <CodeBlock code={`-- PostgreSQL: find and kill blocking sessions
SELECT pid, usename, application_name, state, query
FROM   pg_stat_activity
WHERE  state = 'active';

SELECT pg_terminate_backend(12345);  -- graceful
SELECT pg_cancel_backend(12345);     -- cancel query, keep connection

-- PostgreSQL: explain (ANALYZE actually runs the query!)
EXPLAIN ANALYZE SELECT * FROM employees WHERE department_id = 10;

-- PostgreSQL: table sizes
SELECT
  relname,
  pg_size_pretty(pg_total_relation_size(relid)) AS total,
  pg_size_pretty(pg_relation_size(relid))       AS table_only
FROM pg_stat_user_tables
ORDER BY pg_total_relation_size(relid) DESC;

-- PostgreSQL: gather stats
ANALYZE employees;
VACUUM ANALYZE employees;  -- reclaim space + stats together`} label="sql — PostgreSQL admin" />
    <WarnBox items={[
      "EXPLAIN ANALYZE in PostgreSQL actually executes the query. Use EXPLAIN (without ANALYZE) if you don't want to run it (e.g. for a slow DELETE).",
      "Oracle: stats must be gathered manually or via scheduler for good query plans. PostgreSQL has autovacuum/autoanalyze — usually runs automatically.",
      "pg_terminate_backend kills the connection entirely. pg_cancel_backend cancels only the current query (like Ctrl+C). Prefer cancel first.",
    ]} />
  </div>
);}

function S10() { return (
  <div>
    <Concept>
      If you remember nothing else, remember these mental models — they explain almost every behavioral difference you'll hit.
    </Concept>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
      <div style={{background:"#1a0f06",border:`1px solid ${T.ora}44`,borderRadius:10,padding:"16px"}}>
        <div style={{fontSize:11,fontWeight:700,color:T.ora,letterSpacing:1.5,textTransform:"uppercase",marginBottom:10}}>🟠 Oracle Mental Model</div>
        {[
          "One big instance; users = schemas",
          "DML never commits until YOU say COMMIT",
          "DDL auto-commits — it can't be rolled back",
          "FROM dual required when no table needed",
          "DATE has time component",
          "'' (empty string) IS NULL → true",
          "NVL, DECODE, ROWNUM, VARCHAR2, SYSDATE are the 'accent words'",
          "Licensing is per CPU/user — architecture decisions matter $$$",
        ].map((item,i)=>(
          <div key={i} style={{display:"flex",gap:8,marginBottom:7,fontSize:12.5,color:T.textSec,lineHeight:1.5}}>
            <span style={{color:T.ora,minWidth:16}}>›</span><span>{item}</span>
          </div>
        ))}
      </div>
      <div style={{background:"#071929",border:`1px solid ${T.pg}44`,borderRadius:10,padding:"16px"}}>
        <div style={{fontSize:11,fontWeight:700,color:T.pg,letterSpacing:1.5,textTransform:"uppercase",marginBottom:10}}>🔵 PostgreSQL Mental Model</div>
        {[
          "Many lightweight databases per server instance",
          "Users (roles) and schemas are separate",
          "psql auto-commits each statement by default",
          "DDL is transactional — wrap in BEGIN/ROLLBACK",
          "DATE is date-only; TIMESTAMP has time",
          "'' and NULL are different things",
          "LIMIT, ILIKE, ::cast, SERIAL, \\-commands are the 'accent words'",
          "Open source — no per-row or per-core license cost",
        ].map((item,i)=>(
          <div key={i} style={{display:"flex",gap:8,marginBottom:7,fontSize:12.5,color:T.textSec,lineHeight:1.5}}>
            <span style={{color:T.pg,minWidth:16}}>›</span><span>{item}</span>
          </div>
        ))}
      </div>
    </div>
    <InfoBox title="Portable SQL — Write This, Not That" color={T.green} bgColor="#0a1a10" borderColor="#1E3A52">
      Prefer these forms and your query will work in both databases:
      <ul style={{marginTop:8,paddingLeft:20,lineHeight:2}}>
        <li><code style={{color:T.green}}>COALESCE</code> over <O>NVL</O></li>
        <li><code style={{color:T.green}}>CASE WHEN</code> over <O>DECODE</O></li>
        <li><code style={{color:T.green}}>FETCH FIRST n ROWS ONLY</code> over <O>ROWNUM</O> or <P>LIMIT</P></li>
        <li><code style={{color:T.green}}>GENERATED AS IDENTITY</code> over <O>SEQUENCE+trigger</O> or <P>SERIAL</P></li>
        <li><code style={{color:T.green}}>CURRENT_TIMESTAMP</code> over <O>SYSDATE</O> or <P>NOW()</P></li>
        <li><code style={{color:T.green}}>JOIN … ON</code> syntax over Oracle's old comma-join / <code>(+)</code> outer join</li>
      </ul>
    </InfoBox>
  </div>
);}

// ─── SECTIONS METADATA ────────────────────────────────────────────────────────
const SECTIONS = [
  {id:"home", icon:"🏠", title:"Overview",            sub:"Cheat sheet map and tips"},
  {id:"s1",   icon:"🔌", title:"1. Connecting",        sub:"SQL*Plus vs psql, ports, admin"},
  {id:"s2",   icon:"📚", title:"2. Metadata / Catalog",sub:"Where am I? What exists?"},
  {id:"s3",   icon:"👤", title:"3. Users & Schemas",   sub:"Create, grant, drop — key conceptual diff"},
  {id:"s4",   icon:"🗃",  title:"4. Tables — DDL",      sub:"CREATE, ALTER, DROP, indexes"},
  {id:"s5",   icon:"🔤", title:"5. Data Types",        sub:"Type translation table, DATE gotcha"},
  {id:"s6",   icon:"🔢", title:"6. Auto-increment IDs",sub:"IDENTITY, SEQUENCE, SERIAL"},
  {id:"s7",   icon:"⚡", title:"7. Query Differences", sub:"dual, LIMIT, NVL, ILIKE, dates"},
  {id:"s8",   icon:"🔄", title:"8. Transactions",      sub:"Autocommit, DDL, SAVEPOINT"},
  {id:"s9",   icon:"🛠",  title:"9. Admin Commands",    sub:"Backup, kill, explain, stats"},
  {id:"s10",  icon:"🧠", title:"10. Mental Models",    sub:"One-pager + portable SQL guide"},
];

const sectionComponents = {s1:S1,s2:S2,s3:S3,s4:S4,s5:S5,s6:S6,s7:S7,s8:S8,s9:S9,s10:S10};

function HomePage({ onNavigate }) {
  return (
    <div>
      <div style={{marginBottom:24}}>
        <div style={{fontSize:11,fontWeight:600,color:T.accent,letterSpacing:2,textTransform:"uppercase",marginBottom:8}}>
          DATABASE CHEAT SHEET
        </div>
        <h1 style={{fontSize:24,fontWeight:700,color:T.textPri,margin:"0 0 10px",lineHeight:1.3}}>
          Oracle vs PostgreSQL<br/>
          <span style={{color:T.accent}}>Basic Commands — Side by Side</span>
        </h1>
        <p style={{fontSize:13.5,color:T.textSec,lineHeight:1.7,maxWidth:560,margin:0}}>
          Standard SQL (SELECT / JOINs / GROUP BY) is ~identical in both.
          The differences live in: connecting, metadata queries, data types,
          auto-increment, dates, and admin tasks.
        </p>
      </div>
      <div style={{display:"flex",gap:10,marginBottom:20,flexWrap:"wrap"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,background:"#1a0f06",
          border:`1px solid ${T.ora}44`,borderRadius:20,padding:"4px 12px"}}>
          <span style={{width:8,height:8,borderRadius:"50%",background:T.ora,display:"inline-block"}}/>
          <span style={{fontSize:12,color:T.ora,fontWeight:600}}>Orange = Oracle</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8,background:"#071929",
          border:`1px solid ${T.pg}44`,borderRadius:20,padding:"4px 12px"}}>
          <span style={{width:8,height:8,borderRadius:"50%",background:T.pg,display:"inline-block"}}/>
          <span style={{fontSize:12,color:T.pg,fontWeight:600}}>Blue = PostgreSQL</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8,background:T.card,
          border:`1px solid ${T.border}`,borderRadius:20,padding:"4px 12px"}}>
          <span style={{fontSize:12,color:T.codeKw,fontWeight:600}}>Green = Standard SQL (both)</span>
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        {SECTIONS.filter(s=>s.id!=="home").map((s,i)=>(
          <button key={s.id} onClick={()=>onNavigate(s.id)}
            style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:10,
              padding:"12px 14px",cursor:"pointer",textAlign:"left",fontFamily:"inherit",
              display:"flex",gap:10,alignItems:"flex-start"}}>
            <span style={{fontSize:18,lineHeight:1.2,minWidth:24}}>{s.icon}</span>
            <div>
              <div style={{fontSize:13,fontWeight:600,color:T.textPri,marginBottom:2}}>{s.title}</div>
              <div style={{fontSize:11,color:T.textSec}}>{s.sub}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function DBCommands() {
  const [current, setCurrent] = useState("home");
  const [visited, setVisited] = useState(new Set(["home"]));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const contentRef = useRef(null);

  const navigate = (id) => {
    setCurrent(id);
    setVisited(prev=>new Set([...prev,id]));
    if (contentRef.current) contentRef.current.scrollTop = 0;
  };

  const currentIdx = SECTIONS.findIndex(s=>s.id===current);
  const prevSection = currentIdx>0 ? SECTIONS[currentIdx-1] : null;
  const nextSection = currentIdx<SECTIONS.length-1 ? SECTIONS[currentIdx+1] : null;
  const CurrentComponent = sectionComponents[current];
  const progressPct = Math.round((visited.size/SECTIONS.length)*100);

  return (
    <div style={{display:"flex",flexDirection:"column",height:"calc(100vh - 70px)",
      background:T.bg,color:T.textPri,fontFamily:"system-ui,-apple-system,sans-serif",overflow:"hidden"}}>

      {/* TOP BAR */}
      <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 16px",
        background:T.surface,borderBottom:`1px solid ${T.border}`,flexShrink:0}}>
        <button onClick={()=>setSidebarOpen(o=>!o)}
          style={{background:"none",border:"none",color:T.textSec,cursor:"pointer",fontSize:18,padding:"2px 4px",lineHeight:1}}>☰</button>
        <span style={{fontSize:15,fontWeight:600,color:T.textPri}}>🗄️ Oracle vs PostgreSQL — Command Cheat Sheet</span>
        <div style={{flex:1,height:4,background:T.card,borderRadius:2,overflow:"hidden"}}>
          <div style={{width:`${progressPct}%`,height:"100%",
            background:"linear-gradient(90deg,#FB923C,#38BDF8)",borderRadius:2,transition:"width .4s ease"}} />
        </div>
        <span style={{fontSize:12,color:T.accent,minWidth:36,textAlign:"right",fontWeight:600}}>{progressPct}%</span>
      </div>

      <div style={{display:"flex",flex:1,overflow:"hidden"}}>
        {/* SIDEBAR */}
        {sidebarOpen&&(
          <div style={{width:224,minWidth:224,background:T.surface,borderRight:`1px solid ${T.border}`,overflowY:"auto",flexShrink:0}}>
            <div style={{padding:"8px 6px"}}>
              {SECTIONS.map(s=>(
                <button key={s.id} onClick={()=>navigate(s.id)}
                  style={{display:"flex",alignItems:"center",gap:8,width:"100%",
                    padding:"7px 10px",border:"none",borderRadius:7,cursor:"pointer",
                    background:current===s.id?"rgba(56,189,248,0.08)":"none",fontFamily:"inherit",marginBottom:2,
                    borderLeft:current===s.id?`2px solid ${T.accent}`:"2px solid transparent"}}>
                  <span style={{fontSize:12,minWidth:18}}>{s.icon}</span>
                  <div style={{flex:1,textAlign:"left"}}>
                    <div style={{fontSize:11.5,
                      color:current===s.id?T.accent:visited.has(s.id)?T.green:T.textSec,
                      fontWeight:current===s.id?600:400,lineHeight:1.3}}>
                      {s.title}
                    </div>
                  </div>
                  {visited.has(s.id)&&current!==s.id&&<span style={{fontSize:10,color:T.green}}>✓</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* MAIN CONTENT */}
        <div ref={contentRef} style={{flex:1,overflowY:"auto"}}>
          <div style={{maxWidth:920,margin:"0 auto",padding:"24px 28px"}}>
            {current==="home" ? (
              <HomePage onNavigate={navigate} />
            ) : (
              <div>
                <div style={{marginBottom:22}}>
                  <div style={{fontSize:11,fontWeight:600,color:T.accent,letterSpacing:1.5,
                    textTransform:"uppercase",marginBottom:8}}>
                    Section {currentIdx} of {SECTIONS.length-1}
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
                    <span style={{fontSize:22}}>{SECTIONS[currentIdx].icon}</span>
                    <h1 style={{fontSize:21,fontWeight:700,color:T.textPri,margin:0}}>{SECTIONS[currentIdx].title}</h1>
                  </div>
                  <p style={{fontSize:13,color:T.textSec,margin:0}}>{SECTIONS[currentIdx].sub}</p>
                </div>
                {CurrentComponent&&<CurrentComponent />}
                <div style={{display:"flex",justifyContent:"space-between",marginTop:28,
                  paddingTop:18,borderTop:`1px solid ${T.border}`}}>
                  {prevSection?(
                    <button onClick={()=>navigate(prevSection.id)}
                      style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:9,
                        padding:"9px 16px",cursor:"pointer",color:T.textSec,fontFamily:"inherit",fontSize:13}}>
                      ← {prevSection.icon} {prevSection.title}
                    </button>
                  ):<div/>}
                  {nextSection&&(
                    <button onClick={()=>navigate(nextSection.id)}
                      style={{background:"rgba(56,189,248,0.08)",border:`1px solid ${T.accent}44`,
                        borderRadius:9,padding:"9px 16px",cursor:"pointer",color:T.accent,
                        fontFamily:"inherit",fontSize:13,fontWeight:600}}>
                      {nextSection.icon} {nextSection.title} →
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
